import { Banner, Button } from '@saniplus/ui';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../app/hooks';
import { selectQueueState } from '../features/offline';
import { useServiceWorkerUpdate } from './useServiceWorkerUpdate';

/**
 * "Mise à jour prête" — the full-bleed `info` band, not a card.
 *
 * W7's judgement is explicit about this: the update notice reuses the system's
 * signature band anatomy (glyph, word, sentence, trailing action, radius 0,
 * pushes content down) rather than inventing a second pattern for the same
 * class of fact. A worker who has learned the offline band has learned this
 * one.
 *
 * `role="status"`, never `alert`: a pending update is not urgent and must
 * never steal focus from the punch key.
 *
 * **Recharger is disabled while a punch is in flight or queued.** Applying an
 * update reloads the page; doing that between a tap and the server's answer is
 * how a punch gets lost from the UI's point of view, and "a punch is never
 * silently lost" is the one rule this product cannot break. The band says so
 * in its own sub-label rather than leaving a dead button unexplained.
 */
export function UpdatePrompt() {
  const { t } = useTranslation(['profile', 'common']);
  const { updateAvailable, applyUpdate } = useServiceWorkerUpdate();
  const queue = useAppSelector(selectQueueState);

  const punchInFlight = queue.pending.length > 0 || queue.submitting.length > 0 || queue.syncing;

  if (!updateAvailable) return null;

  return (
    <Banner
      tone="info"
      live="status"
      data-testid="sw-update-prompt"
      title={t('profile:update.word')}
      description={punchInFlight ? t('profile:update.blocked') : t('profile:update.sub')}
      action={
        <Button
          size="md"
          variant="secondary"
          disabled={punchInFlight}
          onClick={applyUpdate}
        >
          {t('profile:update.action')}
        </Button>
      }
      className="shrink-0"
    />
  );
}
